import { motion } from "framer-motion";
import { Mic, Square, Loader2, Check, ChevronRight, AlertTriangle, LucideIcon } from "lucide-react";
import { taskTypeTileShadow } from "@/lib/taskTypeIcon";
import { cn } from "@/lib/utils";

/** El verde de «hecho». Mismo tono que el biológico del catálogo de baldosas. */
const SUCCESS_GREEN = "#16A971";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";

/* -----------------------------------------------------------------
 * Shared header
 * ----------------------------------------------------------------- */
function StateHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  // Sora (la fuente por defecto de la app) y la tinta #13314A del resto de ventanas,
  // en vez del slate-900 suelto.
  return (
    <div className="text-center">
      <h3 className="text-2xl font-bold text-[#13314A]">{title}</h3>
      {subtitle && (
        <p className="text-sm text-slate-600 mt-1 max-w-[300px] mx-auto">{subtitle}</p>
      )}
    </div>
  );
}

/* -----------------------------------------------------------------
 * Initial state
 * ----------------------------------------------------------------- */
interface InitialProps {
  flow: "task" | "scouting";
  onStart: () => void;
}
export function InitialState({ flow, onStart }: InitialProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="initial"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      /* Sin pb: el aire bajo el chip de ejemplo lo pone solo el pb-4 del cuerpo,
         que es el mismo 16px que tiene a los lados. */
      className="flex flex-col items-center gap-6 pt-6"
    >
      <StateHeader
        title={t("voiceModal.initial.title")}
        subtitle={
          flow === "task"
            ? t("voiceModal.initial.subtitleTask")
            : t("voiceModal.initial.subtitleScouting")
        }
      />

      {/* El micro ES el botón de empezar: ya no hay «Iniciar» en el pie, así que se hace
          más grande y lleva su etiqueta debajo para que se vea que se toca. Pastel, con
          el halo del mismo color en vez de un anillo gris. */}
      <button
        onClick={onStart}
        aria-label={t("voiceModal.actions.start")}
        className="group flex flex-col items-center gap-3 transition-transform active:scale-95"
      >
        {/* Azul de la cabecera (#2E84DE, el extremo azul del degradado --band) en el
            halo, el icono y la etiqueta; el relleno se queda en su pastel. */}
        <span className="relative w-28 h-28 rounded-full flex items-center justify-center shadow-[0_10px_30px_-8px_rgba(46,132,222,0.45)] bg-gradient-to-br from-[#eaf1fd] to-[#e6f1ff] group-hover:brightness-[0.98] transition-all">
          <span className="absolute inset-0 rounded-full ring-8 ring-[#2E84DE]/10" />
          <Mic className="relative w-12 h-12 text-[#2E84DE]" strokeWidth={1.9} />
        </span>
        <span className="text-sm font-semibold text-[#2E84DE]">
          {t("voiceModal.actions.start")}
        </span>
      </button>

      {/* Example chip */}
      <div className="w-full bg-white border border-[#e9eff6] rounded-2xl px-4 py-3 shadow-sm">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
          {t("voiceModal.exampleLabel")}
        </p>
        <p className="text-sm text-slate-700">
          {flow === "task"
            ? t("voiceModal.exampleTask")
            : t("voiceModal.exampleScouting")}
        </p>
      </div>
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Recording state
 * ----------------------------------------------------------------- */
interface RecordingProps {
  onStop: () => void;
}
export function RecordingState({ onStop }: RecordingProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="recording"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center gap-6 py-8"
    >
      <StateHeader
        title={t("voiceModal.recording.title")}
        subtitle={t("voiceModal.recording.subtitle")}
      />

      <div className="relative">
        <span className="absolute inset-0 rounded-full bg-rose-300/40 animate-ping" />
        <span className="absolute -inset-3 rounded-full bg-rose-200/30 animate-pulse" />
        <button
          onClick={onStop}
          className="relative w-24 h-24 rounded-full bg-white border-2 border-rose-400 shadow-[0_8px_24px_-6px_rgba(244,63,94,0.4)] flex items-center justify-center transition-transform active:scale-95"
        >
          <Square className="w-8 h-8 text-rose-500 fill-rose-500" />
        </button>
      </div>

      <p className="text-xs text-slate-400">{t("voiceModal.recording.tapToStop")}</p>
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Processing state
 * ----------------------------------------------------------------- */
export function ProcessingState({ transcript }: { transcript?: string }) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="processing"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center gap-5 py-10"
    >
      <div className="relative w-20 h-20 rounded-full bg-white shadow-sm border border-[#e9eff6] flex items-center justify-center">
        <Loader2 className="w-9 h-9 text-blue-500 animate-spin" />
      </div>
      <StateHeader
        title={t("voiceModal.processing.title")}
        subtitle={t("voiceModal.processing.subtitle")}
      />
      {transcript && (
        <div className="w-full bg-white border border-[#e9eff6] rounded-2xl px-4 py-3">
          <p className="text-xs italic text-slate-500 leading-relaxed">"{transcript}"</p>
        </div>
      )}
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Summary row
 * ----------------------------------------------------------------- */
interface SummaryRowSpec {
  icon: LucideIcon;
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  warning?: boolean;
  /** Color sólido de la baldosa (icono en blanco), como en el resto de la app.
   *  Sin él se mantiene la baldosa clarita de siempre, para no tocar otras pantallas. */
  tone?: string;
}

export function SummaryRow({
  spec,
  onClick,
}: {
  spec: SummaryRowSpec;
  onClick?: () => void;
}) {
  const Icon = spec.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-3 hover:bg-slate-50 transition-colors text-left"
    >
      {spec.tone && !spec.warning ? (
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: spec.tone, boxShadow: taskTypeTileShadow(spec.tone) }}
        >
          <Icon className="w-4 h-4 text-white" />
        </div>
      ) : (
        <div
          className={cn(
            "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
            spec.warning ? "bg-amber-50 text-amber-600" : "bg-[#eef4fb] text-black"
          )}
        >
          <Icon className="w-4 h-4" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">
          {spec.label}
        </p>
        <div
          className={cn(
            "text-sm truncate",
            spec.warning ? "text-amber-700" : "text-slate-800",
            spec.emphasis && "font-semibold"
          )}
        >
          {spec.value || <span className="text-slate-400 italic">—</span>}
        </div>
      </div>
      {onClick && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
    </button>
  );
}

/* -----------------------------------------------------------------
 * Summary state
 * ----------------------------------------------------------------- */
interface SummaryProps {
  flow: "task" | "scouting";
  rows: SummaryRowSpec[];
  onEdit: () => void;
  warning?: string | null;
}
export function SummaryState({ flow, rows, warning }: SummaryProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="summary"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-4"
    >
      <StateHeader
        title={t("voiceModal.summary.title")}
        subtitle={
          flow === "task"
            ? t("voiceModal.summary.subtitleTask")
            : t("voiceModal.summary.subtitleScouting")
        }
      />

      {warning && (
        <div className="flex items-start gap-2 rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">{warning}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#e9eff6] divide-y divide-[#eef3f8] overflow-hidden">
        {rows.map((row, i) => (
          <SummaryRow key={i} spec={row} />
        ))}
      </div>
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Edit state wrapper (renders existing form inside themed surface)
 * ----------------------------------------------------------------- */
export function EditState({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="edit"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-4"
    >
      <StateHeader
        title={t("voiceModal.edit.title")}
        subtitle={t("voiceModal.edit.subtitle")}
      />
      <div
        className={cn(
          "bg-white rounded-2xl border border-[#e9eff6] p-4 shadow-sm voice-edit-scope",
          // Scoped polish: inputs, selects, textareas, labels, primary buttons
          "[&_h4]:text-slate-800 [&_h4]:font-semibold",
          "[&_label]:text-[11px] [&_label]:font-semibold [&_label]:uppercase [&_label]:tracking-wider [&_label]:text-slate-700",
          "[&_input]:bg-white [&_input]:shadow-sm [&_input]:border [&_input]:border-[#e2ecf5] [&_input]:rounded-xl [&_input]:text-slate-800",
          "[&_textarea]:bg-white [&_textarea]:shadow-sm [&_textarea]:border [&_textarea]:border-[#e2ecf5] [&_textarea]:rounded-xl [&_textarea]:text-slate-800",
          "[&_[role=combobox]]:bg-white [&_[role=combobox]]:shadow-sm [&_[role=combobox]]:border [&_[role=combobox]]:border-[#e2ecf5] [&_[role=combobox]]:rounded-xl [&_[role=combobox]]:text-slate-800"
        )}
      >
        {children}
      </div>
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Submitting state
 * ----------------------------------------------------------------- */
export function SubmittingState() {
  const { t } = useTranslation();
  return (
    <motion.div
      key="submitting"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center gap-4 py-12"
    >
      <Loader2 className="w-10 h-10 text-primary animate-spin" />
      <p className="text-sm text-slate-600 font-medium">{t("voiceModal.submitting")}</p>
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Success state
 * ----------------------------------------------------------------- */
interface SuccessProps {
  flow: "task" | "scouting";
  rows: SummaryRowSpec[];
  primaryLabel: string;
  onPrimary: () => void;
  onSecondary: () => void;
}
export function SuccessState({ flow, rows }: SuccessProps) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center gap-4 py-2"
    >
      {/* Verde sólido con el tick en blanco, igual que las baldosas de los datos
          de abajo: era el único emblema de la pantalla que seguía siendo un
          degradado clarito con el icono de color. */}
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center"
        style={{ backgroundColor: SUCCESS_GREEN, boxShadow: taskTypeTileShadow(SUCCESS_GREEN) }}
      >
        <Check className="w-8 h-8 text-white" strokeWidth={3} />
      </div>
      <StateHeader
        title={
          flow === "task"
            ? t("voiceModal.success.titleTask")
            : t("voiceModal.success.titleScouting")
        }
        subtitle={
          flow === "task"
            ? t("voiceModal.success.subtitleTask")
            : t("voiceModal.success.subtitleScouting")
        }
      />

      {rows.length > 0 && (
        <div className="w-full bg-white rounded-2xl border border-[#e9eff6] divide-y divide-[#eef3f8] overflow-hidden mt-2">
          {rows.map((row, i) => (
            <SummaryRow key={i} spec={row} />
          ))}
        </div>
      )}
    </motion.div>
  );
}

/* -----------------------------------------------------------------
 * Footer button helpers
 * ----------------------------------------------------------------- */
export function ModalFooter({
  primary,
  secondary,
  warning,
}: {
  primary: { label: string; onClick: () => void; disabled?: boolean; icon?: LucideIcon };
  secondary?: { label: string; onClick: () => void };
  warning?: string;
}) {
  const PrimaryIcon = primary.icon;
  return (
    <div>
      {warning && (
        <p className="text-xs text-amber-600 mb-2 text-center">{warning}</p>
      )}
      <div className="flex gap-2">
        {secondary && (
          <button
            type="button"
            onClick={secondary.onClick}
            className="flex-1 min-h-[44px] px-3 py-2 rounded-2xl bg-white border border-[#cfdcec] text-slate-700 text-sm font-semibold leading-tight text-center hover:bg-slate-50 transition-colors"
          >
            {secondary.label}
          </button>
        )}
        <button
          type="button"
          onClick={primary.onClick}
          disabled={primary.disabled}
          className={cn(
            "flex-1 min-h-[44px] px-3 py-2 flex items-center justify-center gap-2 rounded-2xl text-sm font-semibold leading-tight text-center transition-all text-white",
            primary.disabled
              ? "bg-slate-300 cursor-not-allowed"
              : "ipm-gradient-main shadow-md shadow-primary/25 active:scale-[0.98]"
          )}
        >
          {PrimaryIcon && <PrimaryIcon className="w-4 h-4 shrink-0" />}
          <span className="break-words">{primary.label}</span>
        </button>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Warnings state (missing config)
 * ----------------------------------------------------------------- */
export function WarningsState({
  items,
  onContinue,
}: {
  items: string[];
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  return (
    <motion.div
      key="warnings"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-4"
    >
      <div className="flex items-center gap-2 text-amber-600">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <h4 className="text-sm font-semibold">{t("voiceTask.missingConfig")}</h4>
      </div>
      <ul className="space-y-1.5 bg-white rounded-2xl border border-[#e9eff6] p-3">
        {items.map((it, i) => (
          <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            {it}
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-500">{t("voiceTask.missingConfigHint")}</p>
      <button
        onClick={onContinue}
        className="w-full py-2.5 rounded-2xl bg-[#eef4fb] text-primary text-sm font-semibold hover:bg-[#e2ecf5] transition-colors"
      >
        {t("voiceTask.continueAnyway")}
      </button>
    </motion.div>
  );
}
